import dayjs, { Dayjs } from 'dayjs';
import calendar from 'dayjs/plugin/calendar';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import dayOfYear from 'dayjs/plugin/dayOfYear';
import duration from 'dayjs/plugin/duration';
import localeData from 'dayjs/plugin/localeData';
import relativeTime from 'dayjs/plugin/relativeTime';
import utc from 'dayjs/plugin/utc';

// Import locales directly to ensure they're available
import 'dayjs/locale/cy';
import 'dayjs/locale/da';
import 'dayjs/locale/de';
import 'dayjs/locale/cs';
import 'dayjs/locale/el';
import 'dayjs/locale/en';
import 'dayjs/locale/et';
import 'dayjs/locale/es';
import 'dayjs/locale/fi';
import 'dayjs/locale/fr';
import 'dayjs/locale/it';
import 'dayjs/locale/ka';
import 'dayjs/locale/lt';
import 'dayjs/locale/lv';
import 'dayjs/locale/nb';
import 'dayjs/locale/nl';
import 'dayjs/locale/pl';
import 'dayjs/locale/pt';
import 'dayjs/locale/ro';
import 'dayjs/locale/sk';
import 'dayjs/locale/sv';
import 'dayjs/locale/ar';
import 'dayjs/locale/ur';
import 'dayjs/locale/gu';
import 'dayjs/locale/bn';
import 'dayjs/locale/pa-in';

dayjs.extend(utc);
dayjs.extend(customParseFormat);
dayjs.extend(duration);
dayjs.extend(calendar);
dayjs.extend(relativeTime);
dayjs.extend(localeData);
dayjs.extend(dayOfYear);
dayjs.locale('en');

export const UTC_DATE_TIME_FORMAT = 'YYYY-MM-DDTHH:mm:ss.SSSZ';
export const UTC_DATE_TIME_FORMAT_NO_MILLISECONDS = 'YYYY-MM-DDTHH:mm:ss';
export const DATE_FORMAT = 'DD/MM/YYYY';
export const DATE_FORMAT_FULL_YEAR = 'D MMM YYYY';
export const DATE_FORMAT_FULL_MONTH_FULL_YEAR = 'D MMMM YYYY';
export const DATE_FORMAT_FULL = 'D MMMM YYYY, HH:mm';
export const DATE_FORMAT_FULL_DATE_ONLY = 'DD MMM YYYY';
export const DATE_FORMAT_TIMELINE = 'DD MMM';
export const DATE_FORMAT_MONTH_YEAR = 'MMM YYYY';
export const DATE_FORMAT_MOOD_TRACKER = 'YYYY-MM-DD';
export const DATE_FORMAT_DAY_SHORT = 'D';
export const DATE_FORMAT_HOUR = 'HH:mm';
export const DATE_FORMAT_DAY_FULL = 'dddd';
export const DATE_FORMAT_DEFAULT = 'DD/MM/YYYY';
export const DATE_FORMAT_DMY = 'DD/MM/YYYY';
export const DATE_WEEK_LENGTH = 7;

// Custom calendar config for dayjs (will be localized)
const getCalendarConfig = () => ({
  sameDay: '[Today]',
  nextDay: '[Tomorrow]',
  lastDay: '[Yesterday]',
  nextWeek: '[Next] dddd',
  lastWeek: 'd [days ago]',
  sameElse: DATE_FORMAT_FULL,
});

export const dateOfBirthMinDate = new Date(1850, 1, 1);

/**
 * Parse a DOB string that may arrive as DD/MM/YYYY (from AI onboarding)
 * or as an ISO date string (from the UI).  Returns a valid Dayjs or null.
 */
export const parseDob = (dob: string | null | undefined): Dayjs | null => {
  if (!dob) return null;
  const asDMY = dayjs(dob, DATE_FORMAT_DMY, true);
  if (asDMY.isValid()) return asDMY;
  const asISO = dayjs(dob);
  return asISO.isValid() ? asISO : null;
};

export const formatUtcDate = (utcDate: string, format: string) => {
  return dayjs.utc(utcDate).local().format(format);
};

export const formatDate = (date: string, format: string) => {
  return dayjs(date).format(format);
};

export const formatZuluDate = (date: string, format: string) => {
  return dayjs.utc(date).format(format);
};

export const isSameDateByFormat = (
  date1: string,
  date2: string,
  format: string
) => {
  return formatDate(date1, format) === formatDate(date2, format);
};

export const formatToUtcDate = (date: Date) => {
  return dayjs(date).format(UTC_DATE_TIME_FORMAT);
};

export const formatUtcDateUserFriendly = (
  date: string,
  disableFuture = true
) => {
  if (disableFuture) {
    const now = dayjs();
    const dateObj = dayjs.utc(date);
    const result = dateObj.isBefore(now) ? dateObj : now;
    return result.fromNow();
  } else return dayjs.utc(date).fromNow();
};

// Language code mapping for dayjs locales
const DAYJS_LOCALE_MAP = {
  'en-GB': 'en',
  en: 'en',
  'ro-RO': 'ro',
  ro: 'ro',
  'fr-FR': 'fr',
  fr: 'fr',
  'de-DE': 'de',
  de: 'de',
  'ka-GE': 'ka',
  ka: 'ka',
  'cy-GB': 'cy',
  cy: 'cy',
  'nl-NL': 'nl',
  nl: 'nl',
  'it-IT': 'it',
  it: 'it',
  'el-GR': 'el',
  el: 'el',
  'es-ES': 'es',
  es: 'es',
  'pt-PT': 'pt',
  pt: 'pt',
  'fi-FI': 'fi',
  fi: 'fi',
  'nb-NO': 'nb',
  nb: 'nb',
  'sv-SE': 'sv',
  sv: 'sv',
  'da-DK': 'da',
  da: 'da',
  'pl-PL': 'pl',
  pl: 'pl',
  'lv-LV': 'lv',
  lv: 'lv',
  'lt-LT': 'lt',
  lt: 'lt',
  'cs-CZ': 'cs',
  cs: 'cs',
  'sk-SK': 'sk',
  sk: 'sk',
  'et-EE': 'et',
  et: 'et',
  'ar-AE': 'ar',
  ar: 'ar',
  'ur-PK': 'ur',
  ur: 'ur',
  'gu-IN': 'gu',
  gu: 'gu',
  'bn-BD': 'bn',
  bn: 'bn',
  'pa-IN': 'pa-in',
  pa: 'pa-in',
} as const;

export const changeDateLocale = (language: string) => {
  const dayjsLocale =
    DAYJS_LOCALE_MAP[language as keyof typeof DAYJS_LOCALE_MAP] || 'en';
  dayjs.locale(dayjsLocale);
};

// Get the current dayjs locale
export const getCurrentDateLocale = () => {
  return dayjs.locale();
};

export const validateBirthdate = (date?: Date | Dayjs) => {
  if (!date) {
    return true;
  }
  const fourteenYearsAgo = dayjs().subtract(14, 'years');
  const birthday = dayjs(date);
  return fourteenYearsAgo.isAfter(birthday);
};

export const isAfterDate = (afterDate: string, beforeDate: string) => {
  return dayjs(afterDate).isAfter(dayjs(beforeDate));
};

export const getYearFromDate = (date: string) => {
  return dayjs(date).year();
};

export const getFirstDayOfTheMonth = (date: Dayjs, format: string) => {
  return dayjs(date).startOf('month').format(format);
};

export const getLastDayOfTheMonth = (date: Dayjs, format: string) => {
  return dayjs(date).endOf('month').format(format);
};

export const getPreviousMonth = (date: Dayjs) => {
  return dayjs(date).subtract(1, 'month');
};

export const getNextMonth = (date: Dayjs) => {
  return dayjs(date).add(1, 'month');
};

export const isCurrentMonth = (date: Dayjs) => {
  const now = dayjs();
  return date.month() === now.month() && date.year() === now.year();
};

export const getDatesInInterval = (start: string, end: string) => {
  const dates: Date[] = [];
  let dateStart = dayjs(start).startOf('day');
  const dateEnd = dayjs(end).startOf('day');
  while (dateEnd.diff(dateStart, 'day') >= 0) {
    dates.push(dateStart.toDate());
    dateStart = dateStart.add(1, 'day');
  }
  return dates;
};

export const getArrayWithDaysOfInterval = (start: string, end: string) =>
  getDatesInInterval(start, end).map(date => dayjs(date).format('D MMM'));

export const formatDurationUserFriendly = (date: string) => {
  const now = dayjs();
  const then = dayjs(date);
  const diffSec = now.diff(then, 'second');

  if (diffSec < 60) return 'now';

  const diffMin = now.diff(then, 'minute');
  if (diffMin < 60) return `${diffMin}min`;

  const diffHour = now.diff(then, 'hour');
  if (diffHour < 24) return `${diffHour}h`;

  const diffDay = now.diff(then, 'day');
  if (diffDay < 30) return `${diffDay}d`;

  const diffMonth = now.diff(then, 'month');
  if (diffMonth < 12) return `${diffMonth}mo`;

  const diffYear = now.diff(then, 'year');
  return `${diffYear}y`;
};

/**
 * @param date
 * @returns `true` if date is past a week, otherwise `false`
 */
export const isOlderThanOneWeek = (date: string) => {
  const now = dayjs();
  const days = now.diff(dayjs(date), 'day', true);
  return days >= DATE_WEEK_LENGTH;
};

export const isOlderThanXDays = (date: string, days: number) => {
  const now = dayjs();
  const daysNo = now.diff(dayjs(date), 'day', true);
  return daysNo >= days;
};

/**
 * @param date
 * @param dateFormat `optional`, desired format for date, default is `D MMM YYYY`
 * @returns '30 Oct 2021' if date is past a week, else friendly date:'3 hours ago'
 */
export const formatDateUtcOrFriendly = (date: string, dateFormat?: string) => {
  return isOlderThanOneWeek(date)
    ? formatUtcDate(date, dateFormat || DATE_FORMAT_FULL_YEAR)
    : formatUtcDateUserFriendly(date);
};

/**
 * @param date
 * @returns `Today` or `Yesterday` if no longer than 2 days, otherwise UTC Friendly
 */
export const renderCalendarOrFriendlyDate = (date: string) => {
  const thisDate = dayjs(date);
  if (dayjs().diff(thisDate, 'day') > 1) {
    return formatUtcDateUserFriendly(date);
  }
  return thisDate.calendar(null, getCalendarConfig());
};

export const formatDateForMessages = (date: string) => {
  return isOlderThanOneWeek(date)
    ? formatUtcDate(date, DATE_FORMAT_FULL_YEAR)
    : renderCalendarOrFriendlyDate(date);
};

export const areDatesEqual = (date1: string | Date, date2: string | Date) => {
  const firstDate = dayjs(date1);
  const secondDate = dayjs(date2);
  return (
    firstDate.year() === secondDate.year() &&
    firstDate.month() === secondDate.month() &&
    firstDate.dayOfYear() === secondDate.dayOfYear()
  );
};

/**
 * Syncs dayjs locale with i18next language
 * Call this when language changes to ensure dates are formatted in the correct locale
 */
export const syncDateLocaleWithI18n = (i18nLanguage: string) => {
  changeDateLocale(i18nLanguage);
};

/**
 * Enhanced date formatting that respects current locale
 * @param date
 * @param dateFormat optional, desired format for date, default is `D MMM YYYY`
 * @returns localized date string
 */
export const formatDateUtcOrFriendlyLocalized = (
  date: string,
  dateFormat?: string
) => {
  return isOlderThanOneWeek(date)
    ? formatUtcDate(date, dateFormat || DATE_FORMAT_FULL_YEAR)
    : formatUtcDateUserFriendly(date);
};

/**
 * Enhanced calendar date formatting that respects current locale
 * @param date
 * @returns localized calendar or friendly date
 */
export const renderCalendarOrFriendlyDateLocalized = (date: string) => {
  const thisDate = dayjs(date);
  if (dayjs().diff(thisDate, 'day') > 1) {
    return formatUtcDateUserFriendly(date);
  }
  return thisDate.calendar(null, getCalendarConfig());
};

/**
 * Enhanced message date formatting that respects current locale
 * @param date
 * @returns localized date for messages
 */
export const formatDateForMessagesLocalized = (date: string) => {
  return isOlderThanOneWeek(date)
    ? formatUtcDate(date, DATE_FORMAT_FULL_YEAR)
    : renderCalendarOrFriendlyDateLocalized(date);
};
